import { CREATE_PREFIX } from "./hooks/useCreatableItems";
import { isOptionGroup } from "./types";
import type {
  SelectOption as MultiSelectOption,
  SelectOptionBase as MultiSelectOptionBase,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AccessorFn = (option: any) => string;

export function normalizeOption(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  option: any,
  getValue?: AccessorFn,
  getLabel?: AccessorFn
): MultiSelectOptionBase {
  if (!getValue && !getLabel) return option as MultiSelectOptionBase;
  return {
    ...option,
    value: getValue ? getValue(option) : option?.value,
    label: getLabel ? getLabel(option) : option?.label,
  };
}

export function normalizeOptions(
  options: MultiSelectOption[] | undefined,
  getValue?: AccessorFn,
  getLabel?: AccessorFn
): MultiSelectOption[] | undefined {
  if (!options) return options;
  if (!getValue && !getLabel) return options;
  return options.map(opt =>
    opt && Array.isArray((opt as { options?: unknown[] }).options)
      ? {
          ...opt,
          options: (opt as { options: unknown[] }).options.map(o =>
            normalizeOption(o, getValue, getLabel)
          ),
        }
      : normalizeOption(opt, getValue, getLabel)
  );
}

/** Flatten grouped options into a flat lookup array. */
export function flattenOptions(
  options: MultiSelectOption[]
): MultiSelectOptionBase[] {
  return options.flatMap(opt => (isOptionGroup(opt) ? opt.options : [opt]));
}

/** Check if any option is a group. */
export function hasGroups(options: MultiSelectOption[]): boolean {
  return options.some(isOptionGroup);
}

/** Convert options to base-ui `items` format for filtering support. */
export function toBaseUiItems(
  options: MultiSelectOption[]
): string[] | { label: string; items: string[] }[] {
  if (hasGroups(options)) {
    return options.filter(isOptionGroup).map(g => ({
      label: g.label,
      items: g.options.map(o => o.value),
    }));
  }
  return (options as MultiSelectOptionBase[]).map(o => o.value);
}

/** Build the effective items array, appending a "Create" entry when needed. */
export function computeEffectiveItems(
  children: unknown,
  baseUiItems: string[] | { label: string; items: string[] }[] | undefined,
  showCreateOption: boolean,
  isGrouped: boolean,
  trimmedInput: string
): string[] | { label: string; items: string[] }[] | undefined {
  if (children) return undefined;

  const createValue = showCreateOption
    ? `${CREATE_PREFIX}${trimmedInput}`
    : null;

  if (!baseUiItems && !createValue) return undefined;

  const base = baseUiItems ?? ([] as string[]);

  if (!createValue) return base;

  if (!isGrouped) {
    return [...(base as string[]), createValue];
  }

  return [
    ...(base as { label: string; items: string[] }[]),
    { label: "", items: [createValue] },
  ];
}
